import React, { useState, useEffect, useCallback } from "react";
import {
    Layout, Table, Input, Button, Form, Space, Row, Col,
    Breadcrumb, Modal, message, Typography, Tooltip, Tag, Descriptions, Dropdown
} from "antd";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
    PlusOutlined, EditOutlined, HomeOutlined, EnvironmentOutlined,
    SearchOutlined, CheckCircleFilled, CloseCircleFilled,
    FilterOutlined, ClearOutlined, EyeOutlined, DeleteOutlined, MoreOutlined
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;

const ManageCustomerArea = () => {
    const [areas, setAreas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [viewItem, setViewItem] = useState(null);
    const [addForm] = Form.useForm();
    const [editForm] = Form.useForm();

    // Top filter bar state (server-side search)
    const [search, setSearch] = useState('');
    const [applied, setApplied] = useState({});

    const fetchAreas = useCallback(async (page = 1, pageSize = 10, filters = {}) => {
        try {
            setLoading(true);
            const res = await api.get(`/settings/customerareas/list.php`, {
                params: {
                    page,
                    limit: pageSize,
                    search: filters.search || '',
                },
            });
            setAreas(res.data.dataset || []);
            setPagination({
                current: res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page || 10,
                total: res.data.pagination?.total_records || 0,
            });
        } catch {
            message.error("Failed to fetch customer areas.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAreas(1, 10, {}); }, [fetchAreas]);
    const handleTableChange = (pg) => fetchAreas(pg.current, pg.pageSize, applied);

    const handleFilter = () => {
        const filters = { search: search.trim() };
        setApplied(filters);
        fetchAreas(1, pagination.pageSize, filters);
    };

    const handleClear = () => {
        setSearch('');
        setApplied({});
        fetchAreas(1, pagination.pageSize, {});
    };

    const handleAdd = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/settings/customerareas/add.php', {
                area_name: values.area_name,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Customer area added successfully.");
                setIsAddModalVisible(false);
                fetchAreas(pagination.current, pagination.pageSize, applied);
                addForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to add customer area.");
        } finally { setLoading(false); }
    };

    const openEdit = (record) => {
        setCurrentItem(record);
        editForm.setFieldsValue({
            area_name: record.area_name,
        });
        setIsEditModalVisible(true);
    };

    const handleEdit = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/settings/customerareas/edit.php', {
                area_id: currentItem.area_id,
                area_name: values.area_name,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Customer area updated successfully.");
                setIsEditModalVisible(false);
                fetchAreas(pagination.current, pagination.pageSize, applied);
                editForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update customer area.");
        } finally { setLoading(false); }
    };

    const handleToggle = async (record, checked) => {
        try {
            const res = await api.post('/settings/customerareas/toggle.php', {
                area_id: record.area_id,
                is_active: checked ? 1 : 0,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message);
                fetchAreas(pagination.current, pagination.pageSize, applied);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update status.");
        }
    };

    const handleDelete = async (record) => {
        try {
            setLoading(true);
            const res = await api.post('/settings/customerareas/delete.php', {
                area_id: record.area_id,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Customer area deleted successfully.");
                fetchAreas(pagination.current, pagination.pageSize, applied);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to delete customer area.");
        } finally { setLoading(false); }
    };

    const openView = async (record) => {
        setViewItem(record);
        setIsViewModalVisible(true);
        try {
            const res = await api.get(`/settings/customerareas/detail.php?area_id=${record.area_id}`);
            if (res.data.status === "success") setViewItem(res.data);
        } catch {
            // Keep the row data if the detail call fails
        }
    };

    const columns = [
        {
            title: 'ID', dataIndex: 'area_id', key: 'area_id', width: 80,
        },
        {
            title: 'Area Name', dataIndex: 'area_name', key: 'area_name',
            sorter: (a, b) => (a.area_name || '').localeCompare(b.area_name || ''),
        },
        {
            title: 'Status', dataIndex: 'is_active', key: 'is_active',
            align: 'center', width: 110,
            render: (val) => val == 1
                ? <Tooltip title="Active"><Space size={4}><CheckCircleFilled style={{ color: 'green', fontSize: 16 }} /><Typography.Text style={{ color: 'green' }}>Active</Typography.Text></Space></Tooltip>
                : <Tooltip title="Inactive"><Space size={4}><CloseCircleFilled style={{ color: 'red', fontSize: 16 }} /><Typography.Text style={{ color: 'red' }}>Inactive</Typography.Text></Space></Tooltip>,
        },
        {
            title: 'Action', key: 'action', align: 'center', fixed: 'right', width: 90,
            render: (_, record) => {
                const items = [
                    {
                        key: "view", label: "View", icon: <EyeOutlined />,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); openView(record); },
                    },
                    {
                        key: "edit", label: "Edit", icon: <EditOutlined />,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); openEdit(record); },
                    },
                    {
                        key: "toggle",
                        label: record.is_active == 1 ? "Deactivate" : "Activate",
                        icon: record.is_active == 1 ? <CloseCircleFilled /> : <CheckCircleFilled />,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); handleToggle(record, record.is_active != 1); },
                    },
                    {
                        key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true,
                        onClick: (info) => {
                            info?.domEvent?.stopPropagation();
                            Modal.confirm({
                                title: "Delete Customer Area",
                                content: "Are you sure you want to delete this customer area?",
                                okText: "Yes, Delete",
                                okType: "danger",
                                cancelText: "No",
                                onOk: () => handleDelete(record),
                            });
                        },
                    },
                ];
                return (
                    <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
                        <Button type="text" className="fluid-dots-btn" icon={<MoreOutlined />} />
                    </Dropdown>
                );
            },
        },
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Settings' },
                                { title: 'Customer Areas' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <EnvironmentOutlined /> Customer Areas
                        </h1>
                        <p className="fluid-page-subtitle">Manage customer area locations</p>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <div className="hero-actions" style={{ marginTop: 0, justifyContent: 'flex-end' }}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setIsAddModalVisible(true)}
                                className="hero-action-btn"
                            >
                                Add Customer Area
                            </Button>
                        </div>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <div className="fluid-filter-compact" style={{ marginBottom: 12 }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onPressEnter={handleFilter}
                                    placeholder="Area name"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={4}>
                                <div className="fluid-filter-compact-actions">
                                    <Button type="primary" icon={<FilterOutlined />} onClick={handleFilter} className="fluid-btn-primary">
                                        Apply
                                    </Button>
                                    <Button icon={<ClearOutlined />} onClick={handleClear} className="fluid-btn-secondary">
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={areas}
                            rowKey="area_id"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={handleTableChange}
                            loading={loading}
                            scroll={{ x: 600 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            {/* Add Modal */}
            <Modal
                title="Add Customer Area"
                open={isAddModalVisible}
                onCancel={() => { setIsAddModalVisible(false); addForm.resetFields(); }}
                footer={null}
                width={520}
                destroyOnClose
            >
                <Form form={addForm} layout="vertical" onFinish={handleAdd}>
                    <Form.Item
                        label="Area Name"
                        name="area_name"
                        rules={[{ required: true, message: 'Area name is required' }, { max: 150, message: 'Max 150 characters' }]}
                    >
                        <Input placeholder="e.g. North Zone, Colombo Branch" />
                    </Form.Item>
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setIsAddModalVisible(false); addForm.resetFields(); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>Add Area</Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>

            {/* Edit Modal */}
            <Modal
                title="Edit Customer Area"
                open={isEditModalVisible}
                onCancel={() => { setIsEditModalVisible(false); editForm.resetFields(); }}
                footer={null}
                width={520}
            >
                <Form form={editForm} onFinish={handleEdit} layout="vertical">
                    <Form.Item
                        label="Area Name"
                        name="area_name"
                        rules={[{ required: true, message: 'Area name is required' }, { max: 150, message: 'Max 150 characters' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>Save Changes</Button>
                            <Button danger onClick={() => setIsEditModalVisible(false)}>Cancel</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* View Modal */}
            <Modal
                title={`Customer Area Details${viewItem ? ` - ${viewItem.area_name}` : ""}`}
                open={isViewModalVisible}
                onCancel={() => setIsViewModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsViewModalVisible(false)}>Close</Button>
                ]}
                width={520}
            >
                {viewItem && (
                    <Descriptions column={1} size="small" bordered>
                        <Descriptions.Item label="ID">
                            <Tag color="blue">#{viewItem.area_id}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Area Name">
                            <Typography.Text strong>{viewItem.area_name}</Typography.Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Status">
                            {viewItem.is_active == 1
                                ? <Tag color="green" icon={<CheckCircleFilled />}>Active</Tag>
                                : <Tag color="red" icon={<CloseCircleFilled />}>Inactive</Tag>}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
        </Layout>
    );
};

export default ManageCustomerArea;
